import { Box, Typography, useTheme } from "@outschool/backpack";
import {
  renderCurrencyStringWithCurrencyConversion,
  useCurrencyLocalization,
  useTranslation,
} from "@outschool/localization";
import { useSession } from "@outschool/ui-auth";
import React from "react";

import { useExchangeRate } from "../../../queries/ExchangeRateQueries";
import { useOrderContext } from "../OrderContext";
import { WarningMessages } from "./WarningMessages";

export default function PriceCreditsOutstandingBalanceBreakdown({
  isChargingInLocalCurrency = true,
}: {
  isChargingInLocalCurrency?: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\priceCreditsBreakdown",
  );
  const orderContext = useOrderContext();
  const { pricingBreakdown } = orderContext;
  const { currencyCode } = useCurrencyLocalization();
  const { exchangeRate } = useExchangeRate(currencyCode);
  const { currentUser } = useSession();
  const theme = useTheme();

  if (
    !pricingBreakdown ||
    !pricingBreakdown.subscriptionOutstandingBalanceCredits ||
    !pricingBreakdown.subscriptionOutstandingBalanceCents
  ) {
    return null;
  }

  const styles = {
    container: {
      padding: 24,
      borderRadius: 24,
      marginTop: 16,
      marginBottom: 24,
      background: theme.palette.primary[50],
    },
    row: {
      display: "flex",
      justifyContent: "space-between",
      paddingBottom: 14,
    },
  };

  return (
    <Box>
      <Typography variant="h5">
        <b>{t("Pay for the outstanding balance")}</b>
      </Typography>
      <WarningMessages
        enrollingParent={currentUser}
        pricingOfferState={pricingBreakdown.pricingOfferState}
        pricingOffer={pricingBreakdown.pricingOffer}
      />
      <Box sx={styles.container}>
        <Box sx={styles.row}>
          <Typography>
            {t("Outstanding balance ({{credits}} credits) ", {
              credits: pricingBreakdown.subscriptionOutstandingBalanceCredits,
            })}
          </Typography>
          <Typography>
            {renderCurrencyStringWithCurrencyConversion({
              amountUSDCents:
                pricingBreakdown.subscriptionOutstandingBalanceCents,
              showCurrencyCode: true,
              ...(isChargingInLocalCurrency
                ? {
                    exchangeRate,
                    currencyCode,
                  }
                : // default to USD
                  {}),
            })}
          </Typography>
        </Box>
        {!!pricingBreakdown.storedCreditCents && (
          <Box
            sx={{
              ...styles.row,
              ...{
                color: "success.500",
              },
            }}
          >
            <Typography>{t("Outschool cash")}</Typography>
            <Typography>
              -{" "}
              {renderCurrencyStringWithCurrencyConversion({
                amountUSDCents: pricingBreakdown.storedCreditCents,
                showCurrencyCode: true,
                ...(isChargingInLocalCurrency
                  ? {
                      exchangeRate,
                      currencyCode,
                    }
                  : // default to USD
                    {}),
              })}
            </Typography>
          </Box>
        )}
        {!!pricingBreakdown.pricingOfferCents && (
          <Box
            sx={{
              ...styles.row,
              ...{
                color: "success.500",
              },
            }}
          >
            <Typography>
              {t("{{organizationName}} sponsorship", {
                organizationName: pricingBreakdown?.pricingOffer?.buyerOrgName,
              })}
            </Typography>
            <Typography>
              -{" "}
              {renderCurrencyStringWithCurrencyConversion({
                amountUSDCents: pricingBreakdown.pricingOfferCents,
                showCurrencyCode: true,
                ...(isChargingInLocalCurrency
                  ? {
                      exchangeRate,
                      currencyCode,
                    }
                  : // default to USD
                    {}),
              })}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            paddingTop: 14,
            borderTop: "1px solid",
            borderColor: "neutral.light",
          }}
        >
          <Box sx={{ ...styles.row, paddingBottom: 0 }}>
            <Typography>
              <b>{t("Subtotal")}</b>
            </Typography>
            <Typography>
              <b>
                {renderCurrencyStringWithCurrencyConversion({
                  amountUSDCents: pricingBreakdown.stripeCents,
                  showCurrencyCode: true,
                  ...(isChargingInLocalCurrency
                    ? {
                        exchangeRate,
                        currencyCode,
                      }
                    : // default to USD
                      {}),
                })}
              </b>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
